// frontend/src/lib/rbac.ts
import { useCallback } from 'react';
import catalog from './rbac-catalog.json';
import { useAuthStore } from '../stores/authStore';
import { AREA_OF_ROUTE, TAB_CAP, ROUTE_ANY_CAPS, NAV_ORDER } from './rbac-map';

export interface CatalogCapability {
  key: string;
  area: string;
  level: string;
  label: string;
  description?: string;
  requires?: string;
}
export interface CatalogArea { key: string; label: string }

export const CATALOG = catalog as { areas: CatalogArea[]; levels: Record<string, string>; capabilities: CatalogCapability[] };
export const CAP_BY_KEY: Map<string, CatalogCapability> = new Map(CATALOG.capabilities.map((c) => [c.key, c]));

export const isKeyLevel = (level: string) => level.startsWith('key:');

/** Ícono por llave especial (para el editor de roles y tooltips). */
export const KEY_LEVEL_ICON: Record<string, string> = {
  'key:money': '💰', 'key:spend': '💸', 'key:price': '🏷️', 'key:delete': '🗑️', 'key:brake': '🚨',
  'key:secrets': '🔑', 'key:export': '📤', 'key:buyers': '👤', 'key:sellers': '🏢', 'key:admin': '🔐',
  'key:bulk': '📦',
};

/** Hook: `const can = useCan(); if (can('orders.buy')) ...` */
export function useCan(): (cap: string) => boolean {
  const user = useAuthStore((s) => s.user);
  return useCallback(
    (cap: string) => !!user && (user.is_admin || (user.capabilities ?? []).includes(cap)),
    [user],
  );
}

export { AREA_OF_ROUTE, TAB_CAP, ROUTE_ANY_CAPS, NAV_ORDER };
export { Can, RequireCap, NoAccess } from './Can';

export function useCanAny(caps: string[]): boolean {
  const can = useCan();
  return caps.some((c) => can(c));
}

/** ¿Puede el usuario entrar a esta ruta? Las rutas de ROUTE_ANY_CAPS (ajustes, contactos) abren
 *  con CUALQUIERA de sus pestañas; el resto exige la capacidad del área. Ruta sin mapear: NO se muestra. */
export function routeAllowed(to: string, can: (cap: string) => boolean): boolean {
  const anyCaps = ROUTE_ANY_CAPS[to];
  if (anyCaps) return anyCaps.some((c) => can(c));
  const cap = AREA_OF_ROUTE[to];
  return !!cap && can(cap);
}

/** Primera pantalla a la que el usuario SÍ puede entrar, en el orden del menú.
 *  `null` = no tiene ninguna pantalla (ahí se muestra el aviso de sin acceso, no se redirige). */
export function firstAllowedRoute(can: (cap: string) => boolean): string | null {
  return NAV_ORDER.find((r) => routeAllowed(r, can)) ?? null;
}

/** Hook con la misma regla, para filtrar menús: `const canGo = useCanRoute(); canGo('/finance')`. */
export function useCanRoute(): (to: string) => boolean {
  const can = useCan();
  return useCallback((to: string) => routeAllowed(to, can), [can]);
}

/** Filtra las pestañas de una página según TAB_CAP[page]. Una pestaña sin entrada en el mapa se OCULTA (cerrado por defecto). */
export function visibleTabs<T extends { key: string }>(page: string, tabs: T[], can: (cap: string) => boolean): T[] {
  const map = TAB_CAP[page] ?? {};
  return tabs.filter((t) => !!map[t.key] && can(map[t.key]));
}

/** Devuelve la pestaña activa si está permitida; si no, la primera permitida; si ninguna, null. */
export function pickActiveTab<T extends { key: string }>(
  page: string,
  tabs: T[],
  active: string,
  can: (cap: string) => boolean,
): string | null {
  const vis = visibleTabs(page, tabs, can);
  if (vis.some((t) => t.key === active)) return active;
  return vis[0]?.key ?? null;
}
